import {
  PlayerProfile,
  TournamentCompetition,
  TournamentEntry,
  TournamentPlayer,
  TournamentRegistration,
  TournamentSession,
} from "@mixitone/models";
import { isPresent, isValidEmailAddress, withStateToggle } from "@mixitone/util";
import { AuthController } from "controllers/AuthController";
import { PortalController } from "controllers/PortalController";
import portalFunctions from "lib/portalFunctions";

interface InjectedProps {
  params: {
    portalSlug: string;
    competitionId: string;
  };
}

interface State {
  tournamentLoading: boolean;
  userLoading: boolean;
  saving: boolean;
  tournament: TournamentSession | null;
  competition: TournamentCompetition | null;
  registration?: TournamentRegistration;
  entry?: TournamentEntry;
  formData: {
    name: string;
    email: string;
    mobile: string;
  };
  selectedPaymentMethodId: string | null;
  partnerCode: string | null;
  message: string | null;
  messageType: "success" | "error" | "info" | null;
  registrationComplete: boolean;
  validationEmailSent: boolean;
  validationEmailLink: string | null;
  partnerLink: string | null;
  activeSection: number;
  completedSections: number[];

  partnerEmail: string;
  sendingPartnerInvite: boolean;
  partnerInviteSent: boolean;
  resendingValidationEmail: boolean;
  akahuPolling: boolean;
  /** True after the short poll window while the attempt may still complete later. */
  akahuAwaitingConfirmation: boolean;
  /** In-payment-section status for Akahu (not the page banner). */
  akahuStatusMessage: string | null;
  /** Explicit tone so awaiting copy is not mistaken for a retryable error. */
  akahuStatusIsError: boolean;
}

const AKAHU_PAYMENT_TYPE = "akahu-one-off";
const POLL_INTERVAL_MS = 2500;
const POLL_MAX_ATTEMPTS = 24;
const POLL_SLOW_MESSAGE_AFTER_ATTEMPT = 6;
const AKAHU_AWAITING_CONFIRMATION_MESSAGE =
  "Payment is still being processed by your bank. This can take a while if bank approval is needed. Do not submit another payment. Check the status below, or refresh this page later.";
const AKAHU_CONFIRMING_MESSAGE = "Confirming your payment with the bank...";
const AKAHU_SLOW_CONFIRMING_MESSAGE =
  "Still confirming with your bank. Some payments need extra approval and can take longer.";

export class CompetitionRegistrationPageController extends PortalController<State, InjectedProps> {
  get initialState(): State {
    return {
      tournamentLoading: true,
      userLoading: true,
      saving: false,
      tournament: null,
      competition: null,
      formData: {
        name: "",
        email: "",
        mobile: "",
      },
      selectedPaymentMethodId: null,
      partnerCode: null,
      message: null,
      messageType: null,
      registrationComplete: false,
      validationEmailSent: false,
      validationEmailLink: null,
      partnerLink: null,
      activeSection: 0,
      completedSections: [],
      partnerEmail: "",
      sendingPartnerInvite: false,
      partnerInviteSent: false,
      resendingValidationEmail: false,
      akahuPolling: false,
      akahuAwaitingConfirmation: false,
      akahuStatusMessage: null,
      akahuStatusIsError: false,
    };
  }

  /** Payment step stays locked while polling or waiting on a non-terminal attempt. */
  get akahuPaymentLocked() {
    return this.state.akahuPolling || this.state.akahuAwaitingConfirmation;
  }

  async initialize(props: InjectedProps) {
    // Check if there's a partner code in the URL
    const urlParams = new URLSearchParams(window.location.search);
    this.state.partnerCode = urlParams.get("partnerCode");

    this.restoreValidationEmailSent(props.params.competitionId);

    await this.loadTournamentAndCompetition(props);

    // Watch for auth state changes and reload user data
    const authController = this.get(AuthController);
    let sessionChecked = false;
    this.observe(async () => {
      if (!sessionChecked && authController.state.sessionChecked) {
        sessionChecked = true;
        await this.loadUserData();
      }
      this.state.userLoading = !authController.state.sessionChecked;
    });
  }

  private paymentAttemptStorageKey() {
    return `akahuPaymentAttemptId_${this.state.competition?.id || "unknown"}`;
  }

  private getSavedPaymentAttemptId() {
    return sessionStorage.getItem(this.paymentAttemptStorageKey());
  }

  private savePaymentAttemptId(paymentAttemptId: string) {
    sessionStorage.setItem(this.paymentAttemptStorageKey(), paymentAttemptId);
  }

  private clearSavedPaymentAttemptId() {
    sessionStorage.removeItem(this.paymentAttemptStorageKey());
  }

  private getSelectedPaymentMethod() {
    if (!this.state.selectedPaymentMethodId || !this.state.tournament?.paymentMethods.loaded) {
      return null;
    }
    return (
      this.state.tournament.paymentMethods.find((m) => m.id === this.state.selectedPaymentMethodId) ?? null
    );
  }

  /**
   * Payment step is done when paid (Akahu or admin-confirmed), or when a
   * cash/manual method has been recorded and is awaiting club confirmation.
   */
  private isPaymentStepComplete() {
    const player = this.state.registration?.player;
    if (!player) return false;
    if (player.isPaid) return true;

    const methodId = player.tournament_payment_method_id;
    if (!methodId || !this.state.tournament?.paymentMethods.loaded) return false;

    const method = this.state.tournament.paymentMethods.find((m) => m.id === methodId);
    return Boolean(method && method.payment_type !== AKAHU_PAYMENT_TYPE);
  }

  private getReturnPaymentAttemptId() {
    const urlParams = new URLSearchParams(window.location.search);
    return (
      urlParams.get("paymentAttemptId") ??
      urlParams.get("payment_attempt_id") ??
      this.getSavedPaymentAttemptId()
    );
  }

  /** Path Akahu should send the payer back to after bank authorisation. */
  private buildAkahuReturnPath() {
    const url = new URL(window.location.href);
    const params = new URLSearchParams();
    const partnerCode = url.searchParams.get("partnerCode");
    if (partnerCode) {
      params.set("partnerCode", partnerCode);
    }
    const query = params.toString();
    return query ? `${url.pathname}?${query}` : url.pathname;
  }

  private async sleep(ms: number) {
    await new Promise((resolve) => setTimeout(resolve, ms));
  }

  async changeProps(props: InjectedProps) {
    await this.loadTournamentAndCompetition(props);
  }

  get loading() {
    return this.state.tournamentLoading || this.state.userLoading;
  }

  get isDoubles() {
    return this.state.competition?.entry_type === "doubles";
  }

  /** Whether the user is registering a doubles partner */
  get isPartner() {
    return !!this.state.partnerCode;
  }

  @withStateToggle("tournamentLoading")
  private async loadTournamentAndCompetition(props: InjectedProps) {
    try {
      // Load tournament
      const tournament = await TournamentSession.query()
        .eq("portal_slug", props.params.portalSlug)
        .eq("is_public", true)
        .preload("paymentMethods")
        .first();

      if (!tournament) {
        this.state.message = "Tournament not found";
        this.state.messageType = "error";
        return;
      }

      this.state.tournament = tournament;

      // Load competition
      const competition = await TournamentCompetition.query()
        .eq("id", props.params.competitionId)
        .eq("tournament_session_id", tournament.id)
        .first();

      if (!competition) {
        this.state.message = "Competition not found";
        this.state.messageType = "error";
        return;
      }

      this.state.competition = competition;

      // If there's only one payment method, auto-select it
      if (tournament.paymentMethods.loaded && tournament.paymentMethods.length === 1) {
        const enabledMethods = tournament.paymentMethods.filter((m) => m.is_enabled && !m.is_deleted);
        if (enabledMethods.length === 1) {
          this.state.selectedPaymentMethodId = enabledMethods[0].id!;
        }
      }

      // No need to check registrationAllowed as per feedback
    } catch (error) {
      console.error("Error loading tournament/competition:", error);
      this.state.message = "Error loading tournament details";
      this.state.messageType = "error";
    }
  }

  @withStateToggle("userLoading")
  private async loadUserData(options?: { skipAkahuResume?: boolean }) {
    const authController = this.get(AuthController);
    const user = authController.state.user;

    // Re-check URL parameters for partner code if not already set
    if (!this.state.partnerCode) {
      const urlParams = new URLSearchParams(window.location.search);
      this.state.partnerCode = urlParams.get("partnerCode");
    }

    if (user) {
      // User is authenticated, pre-fill form with user data
      this.state.formData = {
        name: user.user_metadata.name || "",
        email: user.email || "",
        mobile: user.user_metadata.mobile || "",
      };

      // If email is verified and we're on section 1 (name and email),
      // mark it as completed and move to section 2 (mobile)
      if (authController.emailIsVerified && this.state.activeSection <= 1) {
        // Clear validation email sent state since email is now verified
        this.clearValidationEmailSent();
        this.actionCompleteSection(0); // Mark tournament details as completed
        this.actionCompleteSection(1); // Mark name and email as completed
        this.state.activeSection = 2; // Set active section to mobile number
      }

      // Check if user already has a tournament player record for this tournament
      if (this.state.tournament?.id && authController.state.user?.id) {
        try {
          // First get the player profile
          const playerProfile = await PlayerProfile.query()
            .eq("user_id", authController.state.user.id)
            .includeDeleted()
            .first();

          if (playerProfile) {
            // Then check for tournament player
            const existingPlayer = await TournamentPlayer.query()
              .eq("tournament_session_id", this.state.tournament.id)
              .eq("player_profile_id", playerProfile.id)
              .first();

            if (existingPlayer) {
              // Check if already registered for this competition
              this.state.registration = await TournamentRegistration.query()
                .eq("tournament_player_id", existingPlayer.id)
                .eq("tournament_competition_id", this.state.competition?.id || "")
                .preload("entry")
                .preload("player")
                .first();

              if (this.state.registration) {
                this.state.entry = this.state.registration.entry;

                // Check if this is a doubles competition and get the partner code
                if (this.isDoubles) {
                  const currentUrl = window.location.href;

                  if (this.state.registration.partner_code) {
                    const partnerLink = `${currentUrl}${currentUrl.includes("?") ? "&" : "?"}partnerCode=${this.state.registration.partner_code}`;
                    this.state.partnerLink = partnerLink;
                  } else {
                    this.state.partnerLink = null;
                  }
                }

                // The email has been sent if its filled in on the registration
                this.state.partnerInviteSent = isPresent(this.state.registration.partner_email);
                this.state.partnerEmail = this.state.registration.partner_email || "";

                // Mark basic sections as completed
                this.actionCompleteSection(0);
                this.actionCompleteSection(1);
                this.actionCompleteSection(2);

                // Check if payment is required and if the payment step is done
                const paymentRequired = this.state.tournament?.requiresPayment;
                const paymentStepComplete = this.isPaymentStepComplete();

                if (existingPlayer.tournament_payment_method_id) {
                  this.state.selectedPaymentMethodId = existingPlayer.tournament_payment_method_id;
                }

                if (paymentRequired) {
                  if (paymentStepComplete) {
                    // Paid, or cash/manual method recorded awaiting admin confirmation
                    this.actionCompleteSection(3);
                    this.actionCompleteSection(4);
                    this.state.activeSection = 4;
                    this.state.registrationComplete = true;
                    if (this.state.registration.player?.isPaid) {
                      this.clearSavedPaymentAttemptId();
                    }
                  } else {
                    // Payment is required but method not chosen / Akahu not confirmed
                    this.state.activeSection = 3;
                    this.state.registrationComplete = false;
                  }
                } else {
                  // No payment required
                  this.actionCompleteSection(3);
                  this.actionCompleteSection(4);
                  this.state.activeSection = 4;
                  this.state.registrationComplete = true;
                }
              }
            }
          }
        } catch (error) {
          console.error("Error checking existing registration:", error);
        }
      }
    }

    if (!options?.skipAkahuResume) {
      await this.resumeAkahuPaymentIfNeeded();
    }
  }

  private async resumeAkahuPaymentIfNeeded() {
    if (
      !this.state.registration?.player?.id ||
      this.state.registration.player.isPaid ||
      this.state.akahuPolling
    ) {
      return;
    }

    const paymentAttemptId = this.getReturnPaymentAttemptId();
    if (!paymentAttemptId) return;

    // Keep the payer on the payment step while we confirm status.
    this.state.activeSection = 3;

    const urlParams = new URLSearchParams(window.location.search);
    const redirectedWithError =
      urlParams.get("akahu_status") === "error" ||
      urlParams.get("error") ||
      urlParams.get("error_description") ||
      urlParams.get("akahu_error");
    if (redirectedWithError) {
      this.state.akahuAwaitingConfirmation = false;
      this.state.akahuStatusIsError = true;
      this.state.akahuStatusMessage =
        urlParams.get("akahu_error_description") ||
        urlParams.get("error_description") ||
        urlParams.get("akahu_error") ||
        "The payment was not authorised. You can try again when you are ready.";
      this.state.akahuPolling = false;
      return;
    }

    this.savePaymentAttemptId(paymentAttemptId);
    await this.pollAkahuPaymentStatus(paymentAttemptId);
  }

  actionCheckAkahuPaymentStatus() {
    const paymentAttemptId = this.getReturnPaymentAttemptId();
    if (!paymentAttemptId || this.state.akahuPolling) return;
    void this.pollAkahuPaymentStatus(paymentAttemptId);
  }

  private async pollAkahuPaymentStatus(paymentAttemptId: string) {
    if (!this.state.registration?.player?.id) return;

    this.state.akahuPolling = true;
    this.state.akahuAwaitingConfirmation = false;
    this.state.akahuStatusIsError = false;
    this.state.akahuStatusMessage = AKAHU_CONFIRMING_MESSAGE;

    try {
      for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
        const statusResponse = await portalFunctions.getTournamentPaymentAttemptStatus({
          paymentAttemptId,
        });

        const status = String(statusResponse?.status ?? "").toLowerCase();
        const paidAt = statusResponse.paidAt;
        const terminalFailureStatuses = ["failed", "declined", "cancelled", "expired", "error"];
        const isTerminalFailure = terminalFailureStatuses.includes(status);
        const isCompleted = status === "completed" || Boolean(paidAt);

        if (isCompleted) {
          await this.loadUserData({ skipAkahuResume: true });
          if (this.state.registration?.player?.isPaid) {
            this.clearSavedPaymentAttemptId();
            this.state.akahuAwaitingConfirmation = false;
            this.state.akahuStatusIsError = false;
            this.state.akahuStatusMessage = null;
            this.state.message = "Payment confirmed. Your registration is complete.";
            this.state.messageType = "success";
            this.state.registrationComplete = true;
            this.actionCompleteSection(3);
            this.actionCompleteSection(4);
            this.state.activeSection = 4;
            return;
          }
        }

        if (isTerminalFailure) {
          this.clearSavedPaymentAttemptId();
          this.state.akahuAwaitingConfirmation = false;
          this.state.akahuStatusIsError = true;
          this.state.akahuStatusMessage =
            "Payment was not completed. Please try again or choose another payment method.";
          return;
        }

        if (attempt + 1 >= POLL_SLOW_MESSAGE_AFTER_ATTEMPT) {
          this.state.akahuStatusMessage = AKAHU_SLOW_CONFIRMING_MESSAGE;
        }

        await this.sleep(POLL_INTERVAL_MS);
      }

      // Non-terminal after the short poll window: keep locked. PENDING_APPROVAL can last hours.
      this.state.akahuAwaitingConfirmation = true;
      this.state.akahuStatusIsError = false;
      this.state.akahuStatusMessage = AKAHU_AWAITING_CONFIRMATION_MESSAGE;
    } catch (error) {
      console.error("Error polling Akahu payment status:", error);
      // Keep locked: a network blip must not invite a second payment.
      this.state.akahuAwaitingConfirmation = true;
      this.state.akahuStatusIsError = false;
      this.state.akahuStatusMessage =
        "We could not confirm your payment status just now. Do not submit another payment. Check the status below, or refresh this page shortly.";
    } finally {
      this.state.akahuPolling = false;
    }
  }

  actionClearMessage() {
    this.state.message = null;
    this.state.messageType = null;
  }

  private validationEmailSentKey(competitionId: string) {
    return `validationEmailSent_${competitionId}`;
  }

  private validationEmailDataKey(competitionId: string) {
    return `validationEmailData_${competitionId}`;
  }

  private restoreValidationEmailSent(competitionId: string) {
    const wasSent = sessionStorage.getItem(this.validationEmailSentKey(competitionId)) === "true";
    if (!wasSent) return;

    const stored = sessionStorage.getItem(this.validationEmailDataKey(competitionId));
    if (!stored) {
      // Stale flag from before we persisted email/name — clear it so the form is shown again
      sessionStorage.removeItem(this.validationEmailSentKey(competitionId));
      return;
    }

    try {
      const data = JSON.parse(stored) as { name?: string; email?: string };
      if (!data.email) {
        sessionStorage.removeItem(this.validationEmailSentKey(competitionId));
        sessionStorage.removeItem(this.validationEmailDataKey(competitionId));
        return;
      }

      this.state.validationEmailSent = true;
      if (data.name) this.state.formData.name = data.name;
      this.state.formData.email = data.email;
    } catch {
      sessionStorage.removeItem(this.validationEmailSentKey(competitionId));
      sessionStorage.removeItem(this.validationEmailDataKey(competitionId));
    }
  }

  private persistValidationEmailSent(competitionId: string) {
    sessionStorage.setItem(this.validationEmailSentKey(competitionId), "true");
    sessionStorage.setItem(
      this.validationEmailDataKey(competitionId),
      JSON.stringify({
        name: this.state.formData.name,
        email: this.state.formData.email,
      }),
    );
    this.state.validationEmailSent = true;
  }

  clearValidationEmailSent() {
    if (this.state.competition?.id) {
      sessionStorage.removeItem(this.validationEmailSentKey(this.state.competition.id));
      sessionStorage.removeItem(this.validationEmailDataKey(this.state.competition.id));
      this.state.validationEmailSent = false;
      this.state.validationEmailLink = null;
    }
  }

  private applyValidationEmailResponse(response: { success: boolean; action_link?: string }) {
    if (!this.state.competition?.id || !response.success) return false;

    this.persistValidationEmailSent(this.state.competition.id);
    this.state.validationEmailLink = response.action_link || null;
    return true;
  }

  @withStateToggle("resendingValidationEmail")
  async actionResendValidationEmail() {
    if (!this.state.competition?.id) {
      this.state.message = "Competition not found";
      this.state.messageType = "error";
      return;
    }

    const { name, email } = this.state.formData;
    if (!email) {
      this.clearValidationEmailSent();
      this.state.message = "Email address is missing. Enter your email and send the verification again.";
      this.state.messageType = "error";
      return;
    }

    try {
      const signInResponse = await portalFunctions.sendTournamentSignInEmailAction({
        name,
        email,
        competitionId: this.state.competition.id,
        partnerCode: this.state.partnerCode || undefined,
      });

      if (signInResponse.success) {
        this.applyValidationEmailResponse(signInResponse);
        this.state.message = "Verification email sent again. Check your inbox.";
        this.state.messageType = "success";
      } else {
        this.state.message = "Failed to resend verification email. Please try again.";
        this.state.messageType = "error";
      }
    } catch (error) {
      console.error("Error resending verification email:", error);
      this.state.message =
        "Error resending verification email. Please try again or contact support@mixitone.com";
      this.state.messageType = "error";
    }
  }

  actionSetSelectedPaymentMethod(id: string | null) {
    this.state.selectedPaymentMethodId = id;
    // Clear terminal Akahu error copy when the payer picks a method again.
    if (this.state.akahuStatusIsError && !this.akahuPaymentLocked) {
      this.state.akahuStatusMessage = null;
      this.state.akahuStatusIsError = false;
    }
  }

  actionSetActiveSection(sectionIndex: number) {
    // Only allow moving to sections that are completed or the next one
    if (this.canMoveToSection(sectionIndex)) {
      this.state.activeSection = sectionIndex;
    }
  }

  actionCompleteSection(sectionIndex: number) {
    if (!this.state.completedSections.includes(sectionIndex)) {
      this.state.completedSections = [...this.state.completedSections, sectionIndex];
    }
  }

  actionMoveToNextSection() {
    const nextSection = this.state.activeSection + 1;
    this.actionCompleteSection(this.state.activeSection);
    this.actionSetActiveSection(nextSection);
  }

  canMoveToSection(sectionIndex: number): boolean {
    // Always allow moving to completed sections or the tournament details section
    if (this.state.completedSections.includes(sectionIndex) || sectionIndex === 0) {
      return true;
    }

    // Require email verification to move past section 1
    if (sectionIndex > 1) {
      const authController = this.get(AuthController);
      if (!authController.emailIsVerified && sectionIndex > 0) return false;
    }

    // Allow moving to the next section if the previous one is completed
    return this.state.completedSections.includes(sectionIndex - 1);
  }

  @withStateToggle("saving")
  async actionSubmit() {
    if (!this.state.tournament || !this.state.competition) {
      this.state.message = "Tournament or competition not found";
      this.state.messageType = "error";
      return;
    }
    const { competition, activeSection } = this.state;
    if (!competition.id) return;

    try {
      const authController = this.get(AuthController);

      // Handle different sections
      switch (activeSection) {
        case 1: // Email validation section
          if (!authController.state.user || !authController.emailIsVerified) {
            // Send tournament sign-in email to verify the user's email
            const signInResponse = await portalFunctions.sendTournamentSignInEmailAction({
              name: this.state.formData.name,
              email: this.state.formData.email,
              competitionId: competition.id,
              partnerCode: this.state.partnerCode || undefined,
            });

            if (signInResponse.success) {
              this.applyValidationEmailResponse(signInResponse);
              this.actionCompleteSection(1);
            } else {
              this.state.message = "Failed to send verification email";
              this.state.messageType = "error";
            }
            return;
          } else {
            // Email is already verified, clear validation email sent state and move to next section
            this.clearValidationEmailSent();
            this.actionCompleteSection(1);
            this.actionMoveToNextSection();
          }
          break;

        case 2: // Mobile number section
          await portalFunctions.updatePlayerProfile({
            name: this.state.formData.name,
            email: this.state.formData.email,
            mobile: this.state.formData.mobile,
          });

          // Create registration after mobile number is submitted
          await this.createRegistration();

          // Mark this section as completed
          this.actionCompleteSection(2);

          // If payment is required, move to payment section, otherwise to final section
          if (this.state.tournament?.requiresPayment) {
            // Explicitly set the active section to payment (section 3)
            this.state.activeSection = 3;
          } else {
            // Skip payment section
            this.actionCompleteSection(3);
            this.state.activeSection = 4;
          }
          break;

        case 3: // Payment section
          if (this.akahuPaymentLocked) {
            return;
          }

          if (
            this.isDoubles &&
            this.isPartner &&
            this.state.tournament?.payment_by_player_or_entry === "entry"
          ) {
            // If this is a doubles partner and payment is per entry, skip payment
            this.actionSetSelectedPaymentMethod(null);
            // await this.processPayment();
            this.actionMoveToNextSection();
            return;
          }

          if (this.state.tournament?.requiresPayment && !this.state.selectedPaymentMethodId) {
            this.state.message = "Please select a payment method";
            this.state.messageType = "error";
            return;
          }

          // Process payment
          await this.processPayment();

          if (this.isPaymentStepComplete()) {
            // Mark payment section as completed
            this.actionCompleteSection(3);

            // Explicitly set the active section to the final section
            this.state.activeSection = 4;
          }
          break;

        case 4: // Final section
          break;
      }
    } catch (error) {
      console.error("Error registering:", error);
      this.state.message = "Error during registration. Please try again or contact support@mixitone.com";
      this.state.messageType = "error";
    }
  }

  private async createRegistration() {
    if (!this.state.competition?.id) return;

    try {
      // Register for the tournament
      const result = await portalFunctions.tournamentRegistration({
        competitionId: this.state.competition.id,
        partnerCode: this.state.partnerCode || undefined,
      });

      this.state.message = "Registration created successfully!";
      this.state.messageType = "success";
      this.state.registrationComplete = false; // Not complete until payment is processed

      // If this is a doubles competition and we got a partner code, generate the link
      if (this.isDoubles && result.partnerCode) {
        const currentUrl = window.location.href;
        const partnerLink = `${currentUrl}${currentUrl.includes("?") ? "&" : "?"}partnerCode=${result.partnerCode}`;
        this.state.partnerLink = partnerLink;
      }

      // Load the updated registration to get the payment reference
      if (this.state.competition?.id) {
        await this.loadUserData();
      }
    } catch (error) {
      console.error("Error creating registration:", error);
      this.state.message = "Error during registration. Please try again or contact support@mixitone.com";
      this.state.messageType = "error";
    }
  }

  private async processPayment() {
    if (!this.state.registration?.player?.id || !this.state.selectedPaymentMethodId) return;

    try {
      const selectedMethod = this.getSelectedPaymentMethod();
      if (!selectedMethod) {
        this.state.message = "Please select a payment method";
        this.state.messageType = "error";
        return;
      }

      if (this.akahuPaymentLocked) {
        this.state.akahuStatusIsError = false;
        this.state.akahuStatusMessage = AKAHU_AWAITING_CONFIRMATION_MESSAGE;
        return;
      }

      // An open Akahu attempt may still succeed; do not switch to another method.
      if (selectedMethod.payment_type !== AKAHU_PAYMENT_TYPE && this.getSavedPaymentAttemptId()) {
        this.state.message =
          "A bank payment is already in progress for this registration. Do not pay again another way until it fails or is confirmed.";
        this.state.messageType = "error";
        return;
      }

      if (selectedMethod.payment_type === AKAHU_PAYMENT_TYPE) {
        this.state.akahuPolling = true;
        this.state.akahuAwaitingConfirmation = false;
        this.state.akahuStatusIsError = false;
        this.state.akahuStatusMessage = "Redirecting you to your bank to authorise payment...";
        const response = await portalFunctions.initiateTournamentAkahuPayment({
          tournamentPlayerId: this.state.registration.player.id,
          paymentMethodId: this.state.selectedPaymentMethodId,
          returnPath: this.buildAkahuReturnPath(),
        });
        const redirectUrl = response?.redirectUrl;
        const paymentAttemptId = response?.paymentAttemptId;

        if (!redirectUrl || !paymentAttemptId) {
          throw new Error("Akahu payment initiation response missing redirect fields");
        }

        this.savePaymentAttemptId(paymentAttemptId);
        window.location.assign(redirectUrl);
        return;
      }

      // Process payment
      const result = await portalFunctions.tournamentPayment({
        tournamentPlayerId: this.state.registration.player.id,
        paymentMethodId: this.state.selectedPaymentMethodId,
      });

      if (result.success) {
        this.state.message = "Payment method recorded. The club will confirm when your payment is received.";
        this.state.messageType = "success";
        this.state.registrationComplete = true;
        // Clear validation email sent state since registration is now complete
        this.clearValidationEmailSent();
      } else {
        throw new Error("Payment processing failed");
      }
    } catch (error) {
      console.error("Error processing payment:", error);
      if (this.getSelectedPaymentMethod()?.payment_type === AKAHU_PAYMENT_TYPE) {
        this.state.akahuPolling = false;
        this.state.akahuAwaitingConfirmation = false;
        this.state.akahuStatusIsError = true;
        this.state.akahuStatusMessage =
          "Error starting payment. Please try again or contact support@mixitone.com";
      } else {
        this.state.message = "Error processing payment. Please try again or contact support@mixitone.com";
        this.state.messageType = "error";
      }
    } finally {
      // Reload user data to get updated payment status
      if (!this.state.akahuPolling) {
        await this.loadUserData({ skipAkahuResume: true });
      }
    }
  }

  async actionSendPartnerInvite() {
    const email = this.state.partnerEmail.trim();
    if (!this.state.registration?.id || !email) {
      this.state.message = "Partner email is required";
      this.state.messageType = "error";
      return;
    }
    if (!isValidEmailAddress(email)) {
      this.state.message = "Enter a valid email address";
      this.state.messageType = "error";
      return;
    }

    this.state.sendingPartnerInvite = true;

    try {
      const response = await portalFunctions.sendTournamentPartnerInvitation({
        tournamentRegistrationId: this.state.registration.id,
        email,
      });

      if (response.success) {
        this.state.partnerInviteSent = true;
        this.state.partnerEmail = email;
        this.state.message = "Partner invite sent successfully!";
        this.state.messageType = "success";
      } else {
        this.state.message = "Failed to send partner invite. Please try again.";
        this.state.messageType = "error";
      }
    } catch (error) {
      console.error("Error sending partner invite:", error);
      this.state.message = "Error sending partner invite. Please try again or contact support@mixitone.com";
      this.state.messageType = "error";
    } finally {
      this.state.sendingPartnerInvite = false;
    }
  }
}
